// ============================================================================
// Global Search Component
// Feature: 035-app-shell-enhancement
// Created: 2026-01-21
// Description: Command palette component using cmdk library for global search
//              and quick actions. Supports Cmd+K/Ctrl+K shortcut, debounced
//              search, capacity type badges, and integration with existing
//              searchMenuItems utility.
// User Stories: US1 (Global Search), US6 (Keyboard Shortcuts)
// ============================================================================

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Command } from 'cmdk';
import clsx from 'clsx';

import { SearchResults } from './SearchResults';
import { QuickActions } from './QuickActions';
import { SearchResult, SearchableResourceType, CapacityType } from '../../types/shell';
import { useAuth } from '../../hooks/useAuth';
import { searchMenuItems } from '../../utils/navigation/RoleBasedMenuFilter';
import { apiClient } from '../../services/apiClient';
import type { UserRoleValue } from '@shared/types/entities';
import { toRole } from '@shared/access/roles';
import { useAuthStore } from '../../stores/authStore';

/**
 * Entity hit shape returned by GET /api/search (Spec 216 T013).
 */
interface EntitySearchHit {
  type: 'capacity' | 'workspace' | 'lakehouse' | 'warehouse' | 'client' | 'storage_account';
  id: string;
  name: string;
  subtitle: string | null;
  route: string;
}

const ENTITY_ICON: Record<EntitySearchHit['type'], string> = {
  capacity: '⚡',
  workspace: '🏢',
  lakehouse: '💧',
  warehouse: '🏛️',
  client: '🤝',
  storage_account: '🗄️',
};

/**
 * Map an auth role (any case) to UserRoleValue via the ONE shared parser.
 * Spec 285: this was a hand-rolled switch that mapped 'superadmin' → 'Admin' (a leftover
 * from when SuperAdmin was not a real role) — so a SuperAdmin's search silently hid every
 * SuperAdmin-only page. Unknown/legacy values still fall back to the lowest role.
 */
function mapAuthRole(authRole: string | undefined): UserRoleValue {
  return toRole(authRole) ?? 'Viewer';
}

interface GlobalSearchProps {
  /** Whether the search palette is open */
  isOpen: boolean;
  /** Callback when the palette should be closed */
  onClose: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Global search command palette component
 * 
 * Features:
 * - 300ms debounced search
 * - Integration with existing searchMenuItems utility
 * - Capacity type badges in results (when available)
 * - Quick actions for common operations
 * - Responsive modal design
 * - Keyboard navigation support via cmdk
 * 
 * Note: Keyboard shortcuts (Cmd+K/Ctrl+K) are handled by parent component
 */
export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose,
  className,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { user } = useAuth();
  // Spec 285 — the viewing tenant's licenses: search never surfaces a license-gated page.
  const licenses = useAuthStore((s) => s.licenses);
  
  // Focus management refs
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstFocusableRef = useRef<HTMLElement | null>(null);
  const lastFocusableRef = useRef<HTMLElement | null>(null);

  // Debounced search function (300ms delay).
  // Spec 216 T015: results = DB entity hits (GET /api/search — capacities,
  // workspaces, lakehouses, warehouses, clients, storage accounts) merged with
  // client-side, role-filtered menu/page hits.
  const performSearch = useCallback(async (query: string) => {
    const term = query.trim();
    if (!term) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    try {
      // Menu/page hits stay client-side (they ARE role-filtered)
      const menuResults = searchMenuItems(term, mapAuthRole(user?.role), licenses, 10);
      const menuHits: SearchResult[] = menuResults.map(result => ({
        id: `menu-${result.item.path}`,
        name: result.item.label,
        title: result.item.label,
        description: result.item.description || `Navigate to ${result.item.label}`,
        type: getResultType(result.item.path),
        path: result.item.path,
        capacityType: extractCapacityType(result.item),
        icon: getResultIcon(result.item.path, result.item.icon),
        matchType: result.matchType,
        score: result.score,
        lastModified: null,
      }));

      // Entity hits from the backend (min 2 chars — matches the API contract)
      let entityHits: SearchResult[] = [];
      if (term.length >= 2) {
        try {
          const { data } = await apiClient.get<{ results: EntitySearchHit[] }>('/api/search', {
            params: { q: term },
          });
          entityHits = (data.results ?? []).map((hit) => ({
            id: `${hit.type}-${hit.id}`,
            name: hit.name,
            title: hit.name,
            description: hit.subtitle ?? undefined,
            type: hit.type as SearchableResourceType,
            path: hit.route,
            capacityType: null,
            icon: ENTITY_ICON[hit.type],
            lastModified: null,
          }));
        } catch {
          // Entity search failing must not break menu search — degrade silently.
        }
      }

      setSearchResults([...entityHits, ...menuHits]);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [user?.role, licenses]);

  // Helper function to determine result type from path
  const getResultType = (path: string): SearchableResourceType => {
    if (path.includes('/workspace')) return 'workspace';
    if (path.includes('/policies')) return 'policy';
    if (path.includes('/backup')) return 'backup';
    if (path.includes('/restore')) return 'restore';
    if (path.includes('/alert')) return 'alert';
    if (path.includes('/cost')) return 'analytics';
    if (path.includes('/infrastructure')) return 'infrastructure';
    if (path.includes('/setting')) return 'settings';
    return 'page';
  };

  // Helper function to get appropriate icon
  const getResultIcon = (path: string, menuIcon?: string): string => {
    if (menuIcon) return menuIcon;
    
    // Fallback icons based on path
    if (path.includes('/dashboard')) return '📊';
    if (path.includes('/workspace')) return '🏢';
    if (path.includes('/policies')) return '🛡️';
    if (path.includes('/backup')) return '💾';
    if (path.includes('/restore')) return '🔄';
    if (path.includes('/alert')) return '🔔';
    if (path.includes('/cost')) return '💰';
    if (path.includes('/infrastructure')) return '🔧';
    if (path.includes('/setting')) return '⚙️';
    if (path.includes('/user')) return '👥';
    return '📄';
  };

  // Helper function to extract capacity type from menu item (if available)
  const extractCapacityType = (_item: unknown): CapacityType | null => {
    // This would depend on how capacity information is stored in menu items
    // For now, return null as the original menu system may not have this
    return null;
  };

  // Debounce search queries
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedIndex(0);
      // Focus the input when modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Focus trapping for accessibility
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const focusableArray = Array.from(focusableElements) as HTMLElement[];
    firstFocusableRef.current = focusableArray[0];
    lastFocusableRef.current = focusableArray[focusableArray.length - 1];

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab: move backwards
        if (document.activeElement === firstFocusableRef.current) {
          event.preventDefault();
          lastFocusableRef.current?.focus();
        }
      } else {
        // Tab: move forwards
        if (document.activeElement === lastFocusableRef.current) {
          event.preventDefault();
          firstFocusableRef.current?.focus();
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Tab') {
        handleTabKey(event);
      }
    };

    // Add event listener to modal instead of document to avoid conflicts
    modal.addEventListener('keydown', handleKeyDown);

    return () => {
      modal.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/15 dark:bg-black/45 flex items-start justify-center pt-16 px-4">
      <div
        ref={modalRef}
        className={clsx(
          'w-full max-w-2xl bg-[var(--color-bg-card)] rounded-lg shadow-2xl border border-[var(--color-border)]',
          'animate-in fade-in-0 zoom-in-95 duration-200',
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-title"
        aria-describedby="search-description"
      >
        {/* Spec 216 T015: filtering is done by performSearch (server + menu
            utility) — cmdk's own filter is disabled the sanctioned way instead
            of the old forced `filter={() => 1}` hack.
            The input's accessible name goes on `label`: cmdk points the input's
            aria-labelledby at its own hidden <label>, which overrides an
            aria-label on the input (that one left the field unnamed). */}
        <Command className="w-full" shouldFilter={false} label="Search resources and commands">
          {/* Search Input */}
          <div className="flex items-center px-4 border-b border-[var(--color-border)]">
            {/* Hidden heading for screen readers */}
            <h2 id="search-title" className="sr-only">
              Global Search and Command Palette
            </h2>
            <p id="search-description" className="sr-only">
              Search for resources, policies, and commands. Use arrow keys to navigate results.
            </p>

            <div className="mr-3 text-[var(--color-text-secondary)]" aria-hidden="true">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <Command.Input
              ref={inputRef}
              className="flex-1 py-4 text-lg outline-none bg-transparent text-[var(--color-text-primary)] placeholder-gray-400"
              placeholder="Search capacities, workspaces, lakehouses, warehouses, clients, storage & pages…"
              value={searchQuery}
              onValueChange={setSearchQuery}
              autoFocus
            />
            <kbd className="ml-3 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded">
              ⌘K
            </kbd>
            {isSearching && (
              <div className="ml-3 text-[var(--color-text-secondary)]" role="status" aria-label="Searching">
                <div className="animate-spin w-4 h-4 border-2 border-[var(--color-border)] border-t-[var(--color-text-secondary)] rounded-full" />
              </div>
            )}
          </div>

          {/* Results Container */}
          <Command.List className="max-h-96 overflow-y-auto">
            {searchQuery.trim() ? (
              <>
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <Command.Group heading="Search Results">
                    {/* SearchResults navigates to result.path on select */}
                    <SearchResults
                      results={searchResults}
                      query={searchQuery}
                      onSelect={() => onClose()}
                    />
                  </Command.Group>
                )}
                
                {/* No Results */}
                {!isSearching && searchQuery.trim() && searchResults.length === 0 && (
                  <Command.Empty className="py-6 text-center text-sm text-[var(--color-text-secondary)]">
                    No results found for "{searchQuery}"
                  </Command.Empty>
                )}
              </>
            ) : (
              /* Quick Actions (shown when no search query) */
              <Command.Group heading="Quick Actions">
                {/* QuickActions runs each action's own navigate() */}
                <QuickActions onAction={() => onClose()} />
              </Command.Group>
            )}
          </Command.List>

          {/* Footer with help text */}
          <div className="px-4 py-2 text-xs text-[var(--color-text-secondary)] border-t border-[var(--color-border)] bg-[var(--color-bg-subtle)] flex justify-between">
            <span>
              Use ↑↓ to navigate, ↵ to select, ESC to close
            </span>
            <span>
              Powered by Command Palette
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
};

export default GlobalSearch;
